/*
 * EKATÒ favourites ("Favoritos"). Saved products live in this browser only
 * (localStorage, with an in-memory fallback when storage is blocked), so no
 * account or app is needed. Loaded only when the theme setting
 * enable_wishlist is on.
 *
 * - [data-wishlist-toggle] buttons save or remove their product (aria-pressed).
 * - [data-wishlist-count] elements show how many products are saved.
 * - [data-wishlist-page] is a favourites list (header drawer or favourites
 *   page). It lists the saved products, refreshes price and availability
 *   from /products/<handle>.js and shares them as ?items=handle,handle. The
 *   page also shows a list someone shared through that link.
 * - Saving a product sends "Added to Wishlist" to Klaviyo. Klaviyo only keeps
 *   it for identified visitors, which is what back-in-stock or low-stock
 *   flows need; removing sends "Removed from Wishlist".
 */
(() => {
  const STORAGE_KEY = 'ekato-wishlist-v1';
  const MAX_ITEMS = 100;

  function createStore(storage) {
    let memory = [];

    const clean = (value) =>
      Array.isArray(value) ? value.filter((item) => item && typeof item.handle === 'string' && item.handle) : [];

    const read = () => {
      try {
        const raw = storage && storage.getItem(STORAGE_KEY);
        if (raw != null) memory = clean(JSON.parse(raw));
      } catch (error) {
        // Corrupt or blocked storage: keep what this page already has.
      }
      return memory.slice();
    };

    const write = (items) => {
      memory = clean(items).slice(0, MAX_ITEMS);
      try {
        if (storage) storage.setItem(STORAGE_KEY, JSON.stringify(memory));
      } catch (error) {
        // Storage full or blocked: the list still works for this page view.
      }
      return memory.slice();
    };

    return {
      read,
      has: (handle) => read().some((item) => item.handle === handle),
      add(product) {
        const items = read().filter((item) => item.handle !== product.handle);
        items.unshift({ ...product, addedAt: Date.now() });
        return write(items);
      },
      remove: (handle) => write(read().filter((item) => item.handle !== handle)),
      update(handle, fields) {
        return write(read().map((item) => (item.handle === handle ? { ...item, ...fields } : item)));
      },
    };
  }

  const root = typeof window !== 'undefined' ? window : globalThis;
  let storage = null;
  try {
    storage = root.localStorage;
  } catch (error) {
    storage = null;
  }
  const store = createStore(storage);
  root.EkatoWishlist = { createStore, store, STORAGE_KEY };

  if (typeof document === 'undefined' || !document.querySelectorAll) return;

  const productFrom = (button) => ({
    handle: button.dataset.productHandle,
    id: Number(button.dataset.productId) || null,
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || '',
    image: button.dataset.productImage || '',
    price: Number(button.dataset.productPrice) || 0,
  });

  function track(event, product) {
    try {
      if (!root.klaviyo || typeof root.klaviyo.push !== 'function') return;
      const origin = root.location ? root.location.origin : '';
      root.klaviyo.push([
        'track',
        event,
        {
          ProductID: product.id,
          ProductName: product.title,
          ProductHandle: product.handle,
          URL: product.url ? origin + product.url : '',
          ImageURL: product.image,
          Price: product.price / 100,
        },
      ]);
    } catch (error) {
      // Analytics never blocks saving.
    }
  }

  function syncButtons() {
    const items = store.read();
    const saved = new Set(items.map((item) => item.handle));

    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const pressed = saved.has(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(pressed));
      const label = pressed ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    });

    // Only touch text that changed: the observer below watches the DOM, and
    // rewriting identical text would wake it again on every frame.
    const setText = (node, text) => {
      if (node.textContent !== text) node.textContent = text;
    };

    document.querySelectorAll('[data-wishlist-count]').forEach((counter) => {
      setText(counter, items.length > 99 ? '99+' : String(items.length));
      counter.hidden = items.length === 0;
    });

    document.querySelectorAll('[data-wishlist-count-label]').forEach((label) => {
      setText(label, (label.dataset.template || '').replace('__count__', String(items.length)));
    });
  }

  function changed() {
    syncButtons();
    renderPages();
    document.dispatchEvent(new CustomEvent('ekato:wishlist-change', { detail: { items: store.read() } }));
  }

  // Capture phase: hearts sit inside product cards whose image or whole card
  // navigates on click. Handle the heart first and stop the event there.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('[data-wishlist-toggle]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();

      const product = productFrom(button);
      if (!product.handle) return;
      if (store.has(product.handle)) {
        store.remove(product.handle);
        track('Removed from Wishlist', product);
      } else {
        store.add(product);
        track('Added to Wishlist', product);
      }
      changed();
    },
    true
  );

  // Another tab saved or removed something.
  root.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) changed();
  });

  /* Favourites list: header drawer and favourites page */

  const refreshed = new Set();
  // Products from a shared link (?items=a,b): fetched, never stored.
  const sharedProducts = new Map();
  const HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/;

  const sharedHandles = () => {
    try {
      const raw = new URLSearchParams(root.location.search).get('items');
      if (!raw) return null;
      const handles = raw.split(',').map((handle) => handle.trim().toLowerCase()).filter((handle) => HANDLE.test(handle));
      return handles.length ? Array.from(new Set(handles)).slice(0, 50) : null;
    } catch (error) {
      return null;
    }
  };

  function formatMoney(page, cents) {
    try {
      return new Intl.NumberFormat(page.dataset.locale || undefined, {
        style: 'currency',
        currency: page.dataset.currency || 'EUR',
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function card(page, item) {
    const template = page.querySelector('template[data-wishlist-item]');
    const node = template.content.firstElementChild.cloneNode(true);

    node.querySelectorAll('[data-item-link]').forEach((link) => {
      link.href = item.url || '#';
    });
    const image = node.querySelector('[data-item-image]');
    if (image) {
      if (item.image) {
        image.src = item.image;
        image.alt = item.title;
      } else {
        image.remove();
      }
    }
    node.querySelector('[data-item-title]').textContent = item.title;
    const priceNode = node.querySelector('[data-item-price]');
    const percent = Number(page.dataset.earlyPercent);
    const start = Number(page.dataset.earlyStart);
    const end = Number(page.dataset.earlyEnd);
    if (String(item.id) === page.dataset.earlyProductId && percent > 0 && percent < 100 && start > 0 && end > start) {
      const enabled = start <= Date.now() && Date.now() < end;
      const base = formatMoney(page, item.price);
      const sale = formatMoney(page, Math.round(item.price * (100 - percent) / 100));
      priceNode.classList.add('ekato-promo-price');
      priceNode.setAttribute('data-early-access-window', '');
      priceNode.setAttribute('data-early-access-price', '');
      Object.assign(priceNode.dataset, { earlyStart: start, earlyEnd: end, earlyBase: base, earlySale: sale });
      const current = document.createElement('span');
      current.setAttribute('data-early-current', '');
      current.textContent = enabled ? sale : base;
      const original = document.createElement('s');
      original.setAttribute('data-early-original', '');
      original.className = 'compare-at-price';
      original.textContent = base;
      original.hidden = !enabled;
      const badge = document.createElement('span');
      badge.setAttribute('data-early-badge', '');
      badge.className = 'ekato-promo-price__badge';
      badge.textContent = `−${percent}%`;
      badge.hidden = !enabled;
      priceNode.replaceChildren(current, original, badge);
    } else {
      priceNode.textContent = item.price ? formatMoney(page, item.price) : '';
    }
    node.querySelector('[data-item-status]').hidden = item.available !== false;

    // The bookmark saves or removes; syncButtons sets its pressed state.
    const toggle = node.querySelector('[data-wishlist-toggle]');
    toggle.dataset.productHandle = item.handle;
    toggle.dataset.productId = item.id || '';
    toggle.dataset.productTitle = item.title;
    toggle.dataset.productUrl = item.url || '';
    toggle.dataset.productImage = item.image || '';
    toggle.dataset.productPrice = item.price || 0;
    toggle.dataset.labelAdd = (toggle.dataset.labelAddTemplate || '').replace('__title__', item.title);
    toggle.dataset.labelRemove = (toggle.dataset.labelRemoveTemplate || '').replace('__title__', item.title);
    const saved = store.has(item.handle);
    toggle.setAttribute('aria-pressed', String(saved));
    toggle.setAttribute('aria-label', saved ? toggle.dataset.labelRemove : toggle.dataset.labelAdd);

    return node;
  }

  const fromProductJson = (product) => {
    const image = product.featured_image || '';
    return {
      handle: product.handle,
      id: product.id,
      title: product.title,
      url: product.url,
      image: image.startsWith('//') ? `https:${image}` : image,
      price: product.price,
      available: product.available,
    };
  };

  function fetchProduct(handle) {
    const base = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    return fetch(`${base}products/${encodeURIComponent(handle)}.js`).then((response) => {
      if (response.status === 404) return { gone: true };
      return response.ok ? response.json() : null;
    });
  }

  // Saved items: refresh price, availability and image once per page view.
  function refresh(item) {
    if (refreshed.has(item.handle)) return;
    refreshed.add(item.handle);
    fetchProduct(item.handle)
      .then((product) => {
        if (!product) return;
        store.update(item.handle, product.gone ? { available: false } : fromProductJson(product));
        renderPages();
        syncButtons();
      })
      .catch(() => {});
  }

  function loadShared(handle) {
    if (sharedProducts.has(handle)) return;
    sharedProducts.set(handle, null);
    fetchProduct(handle)
      .then((product) => {
        if (!product || product.gone) {
          sharedProducts.delete(handle);
          return;
        }
        sharedProducts.set(handle, fromProductJson(product));
        renderPages();
        syncButtons();
      })
      .catch(() => {});
  }

  function renderPages() {
    const shared = sharedHandles();

    document.querySelectorAll('[data-wishlist-page]').forEach((page) => {
      const showShared = shared && page.hasAttribute('data-wishlist-shared-view');
      let items;
      if (showShared) {
        shared.forEach(loadShared);
        items = shared.map((handle) => sharedProducts.get(handle)).filter(Boolean);
      } else {
        items = store.read();
        items.forEach(refresh);
      }

      const list = page.querySelector('[data-wishlist-list]');
      const empty = page.querySelector('[data-wishlist-empty]');
      const note = page.querySelector('[data-wishlist-shared-note]');
      const shareRow = page.querySelector('[data-wishlist-share-row]');

      list.replaceChildren(...items.map((item) => card(page, item)));
      list.hidden = items.length === 0;
      empty.hidden = items.length > 0 || Boolean(showShared);
      if (note) note.hidden = !showShared;
      if (shareRow) shareRow.hidden = Boolean(showShared) || items.length === 0;
    });
  }

  /* Share: native share sheet on phones, otherwise copy the link. */

  function shareLink(page) {
    const handles = store.read().map((item) => item.handle);
    const url = `${page.dataset.shareUrl}?items=${handles.map(encodeURIComponent).join(',')}`;
    const status = page.querySelector('[data-wishlist-share-status]');
    const say = (text) => {
      if (status) status.textContent = text || '';
    };

    if (root.navigator && typeof root.navigator.share === 'function') {
      root.navigator.share({ title: page.dataset.shareTitle, url }).catch(() => {});
      return;
    }
    if (root.navigator && root.navigator.clipboard) {
      root.navigator.clipboard
        .writeText(url)
        .then(() => say(page.dataset.labelCopied))
        .catch(() => say(`${page.dataset.labelShareError} ${url}`));
    } else {
      say(`${page.dataset.labelShareError} ${url}`);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-wishlist-share]');
    if (!button) return;
    const page = button.closest('[data-wishlist-page]');
    if (page) shareLink(page);
  });

  // Cards also arrive later (filters, recommendations, load more): sync new hearts.
  let pending = false;
  const observer = new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      syncButtons();
    });
  });

  const start = () => {
    syncButtons();
    renderPages();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
